import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../../contexts/StoreProvider';
import { Heart, ArrowLeft, Plus, Minus, ShoppingCart } from 'lucide-react';
import alertify from 'alertifyjs';

const ProductDetailScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { products, toggleFavorite, addToCart } = useStore();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState('');
  const [selectedVariant, setSelectedVariant] = useState(null);

  useEffect(() => {
    // If products are loaded, find the product. If not found, one might typically fetch it individually.
    // For now, assuming products list holds it.
    const p = products.find(prod => prod.id === id);
    if (p) {
      setProduct(p);
      setActiveImage(p.images && p.images.length > 0 ? p.images[0].image : null);
      if (p.variants && p.variants.length > 0) {
        setSelectedVariant(p.variants[0]);
      }
    }
  }, [id, products]);

  if (!product) {
    return (
      <div className="flex justify-center items-center py-40">
        <div className="text-gray-500">Loading product details...</div>
      </div>
    );
  }

  const allImages = (product.images || []).map(img => img.image).filter(Boolean);

  const getMeasurements = (variant) => {
    if (!variant) return [];
    const fields = ['Length', 'Width', 'Height', 'Waist', 'Chest', 'Shoulder', 'Sleeves', 'Inseam', 'Hips', 'Neck'];
    const measures = [];
    fields.forEach(field => {
      const key = `measurement_${field.toLowerCase()}`;
      if (variant[key]) measures.push({ label: field, value: variant[key] });
    });
    return measures;
  };

  return (
    <div className="bg-white">
      {/* Back Button */}
      <button 
        onClick={() => navigate(-1)}
        className="flex items-center text-sm font-semibold tracking-wider uppercase mb-8 hover:text-gray-600 transition-colors"
      >
        <ArrowLeft size={16} className="mr-2" />
        Back to Store
      </button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        {/* Images Column */}
        <div className="flex flex-col-reverse md:flex-row gap-4">
          {/* Thumbnails */}
          {allImages.length > 1 && (
            <div className="flex md:flex-col gap-4 overflow-x-auto md:overflow-y-auto md:w-24 flex-shrink-0 hide-scrollbar">
              {allImages.map((imgUrl, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImage(imgUrl)}
                  className={`relative w-20 h-24 flex-shrink-0 border-2 transition-all ${
                    activeImage === imgUrl ? 'border-black' : 'border-transparent hover:border-gray-300'
                  }`}
                >
                  <img src={imgUrl} alt={`Thumbnail ${idx}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          
          {/* Main Image */}
          <div className="flex-1 relative aspect-[3/4] bg-gray-100">
            {activeImage ? (
              <img src={activeImage} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">No Image</div>
            )}
            <button
              onClick={() => toggleFavorite(product.id)}
              className="absolute top-4 right-4 p-3 bg-white/90 backdrop-blur rounded-full hover:bg-white shadow-sm transition-colors"
            >
              <Heart
                size={24}
                className={product.isFavorite ? "fill-red-500 text-red-500" : "text-black"}
              />
            </button>
          </div>
        </div>

        {/* Details Column */}
        <div className="flex flex-col">
          <span className="text-xs font-bold tracking-[0.2em] text-gray-500 uppercase mb-2">
            {product.category_detail?.name || 'GENERAL'}
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold text-black mb-4">
            {product.name}
          </h1>
          <p className="text-2xl font-semibold text-black mb-8">
            ₵{((selectedVariant ? parseFloat(selectedVariant.price) : parseFloat(product.price)) * quantity).toFixed(2)}
          </p>

          {product.variants && product.variants.length > 0 && (
            <div className="mb-8">
              <span className="text-sm font-semibold uppercase tracking-wider block mb-3">Select Size</span>
              <div className="flex flex-wrap gap-3">
                {product.variants.map(variant => (
                  <button
                    key={variant.id}
                    onClick={() => { setSelectedVariant(variant); setQuantity(1); }}
                    className={`px-4 py-2 border font-medium transition-colors ${
                      selectedVariant?.id === variant.id ? 'bg-black text-white border-black' : 'bg-white text-black border-gray-300 hover:border-black'
                    }`}
                  >
                    {variant.size}
                  </button>
                ))}
              </div>
              
              {selectedVariant && getMeasurements(selectedVariant).length > 0 && (
                <div className="mt-6">
                  <span className="text-sm font-semibold uppercase tracking-wider block mb-3">Measurements</span>
                  <div className="flex flex-wrap gap-2">
                    {getMeasurements(selectedVariant).map((m, idx) => (
                      <div key={idx} className="bg-gray-100 px-3 py-1.5 rounded-md border border-gray-200 text-sm font-medium text-gray-800">
                        {m.label}: {m.value}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="prose prose-sm sm:prose-base text-gray-600 mb-8 max-w-none">
            <p>{product.description || 'No description available for this product.'}</p>
          </div>

          <div className="mt-auto">
            <div className="flex items-center space-x-4 mb-6">
              <span className="text-sm font-semibold uppercase tracking-wider">Quantity</span>
              <div className="flex items-center border border-black">
                <button 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-2 hover:bg-gray-100 transition-colors"
                >
                  <Minus size={16} />
                </button>
                <span className="px-4 py-2 font-semibold min-w-[3rem] text-center">{quantity}</span>
                <button 
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-4 py-2 hover:bg-gray-100 transition-colors"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            <button
              onClick={() => {
                if (product.variants?.length > 0 && !selectedVariant) {
                  alertify.error('Please select a size first');
                  return;
                }
                addToCart(product, quantity, selectedVariant?.id);
                setQuantity(1); // Reset after adding
              }}
              className="w-full bg-black text-white py-4 px-8 flex items-center justify-center space-x-2 font-bold tracking-widest uppercase hover:bg-gray-900 transition-colors"
            >
              <ShoppingCart size={20} />
              <span>Add to Cart</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailScreen;
